import type { PointerEvent as ReactPointerEvent } from 'react';
import { Box } from '@mui/material';
import type { SlideElement } from '../types/slide';

type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const HANDLES: { id: Handle; left: string; top: string; cursor: string }[] = [
  { id: 'nw', left: '0%', top: '0%', cursor: 'nwse-resize' },
  { id: 'n', left: '50%', top: '0%', cursor: 'ns-resize' },
  { id: 'ne', left: '100%', top: '0%', cursor: 'nesw-resize' },
  { id: 'e', left: '100%', top: '50%', cursor: 'ew-resize' },
  { id: 'se', left: '100%', top: '100%', cursor: 'nwse-resize' },
  { id: 's', left: '50%', top: '100%', cursor: 'ns-resize' },
  { id: 'sw', left: '0%', top: '100%', cursor: 'nesw-resize' },
  { id: 'w', left: '0%', top: '50%', cursor: 'ew-resize' },
];

const MIN_SIZE = 16;
const GRID = 4;

interface Props {
  el: SlideElement;
  /** Canvas render scale, so handles keep a constant on-screen size. */
  scale: number;
  onResizeStart: () => void;
  onResize: (box: Pick<SlideElement, 'x' | 'y' | 'w' | 'h'>) => void;
}

/** Eight resize handles around the selected element, drawn in slide coordinates. Shift keeps the aspect ratio. */
export function SelectionHandles({ el, scale, onResizeStart, onResize }: Props) {
  const size = 10 / scale;

  const start = (handle: Handle) => (e: ReactPointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const sx = e.clientX;
    const sy = e.clientY;
    const orig = { x: el.x, y: el.y, w: el.w, h: el.h };
    const ratio = orig.w / orig.h;
    let started = false;

    const move = (ev: PointerEvent) => {
      const dx = (ev.clientX - sx) / scale;
      const dy = (ev.clientY - sy) / scale;
      if (!started) {
        started = true;
        onResizeStart();
      }
      let { x, y, w, h } = orig;
      if (handle.includes('e')) w = orig.w + dx;
      if (handle.includes('w')) w = orig.w - dx;
      if (handle.includes('s')) h = orig.h + dy;
      if (handle.includes('n')) h = orig.h - dy;

      if (ev.shiftKey && handle.length === 2) {
        if (Math.abs(w - orig.w) / orig.w > Math.abs(h - orig.h) / orig.h) h = w / ratio;
        else w = h * ratio;
      }

      w = Math.max(MIN_SIZE, Math.round(w / GRID) * GRID);
      h = Math.max(MIN_SIZE, Math.round(h / GRID) * GRID);
      if (handle.includes('w')) x = orig.x + orig.w - w;
      if (handle.includes('n')) y = orig.y + orig.h - h;
      onResize({ x, y, w, h });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <Box
      aria-hidden
      sx={{ position: 'absolute', pointerEvents: 'none', zIndex: 1000 }}
      style={{ left: el.x, top: el.y, width: el.w, height: el.h }}
    >
      {HANDLES.map((hd) => (
        <Box
          key={hd.id}
          onPointerDown={start(hd.id)}
          sx={{
            position: 'absolute',
            pointerEvents: 'auto',
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'primary.main',
            borderRadius: '2px',
            transform: 'translate(-50%, -50%)',
          }}
          style={{
            left: hd.left,
            top: hd.top,
            width: size,
            height: size,
            borderWidth: 1.5 / scale,
            cursor: hd.cursor,
          }}
        />
      ))}
    </Box>
  );
}
